import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import {
  markAllNotificationsSeen,
  markNotificationSeen,
  notificationsSignal,
  unreadCountSignal,
} from '../realtime/notificationsStore';
import type { NotificationDto } from '../api/types';
import './NotificationBell.css';

/**
 * Bell icon in the top bar. Shows the current unread count; clicking opens
 * a dropdown with the latest ~10 notifications. Each item deep-links to
 * `/plants/{plantId}/charts?annotation={sourceId}` — the chart page reads
 * the query on mount, jumps to the annotation's range, and pops the detail
 * popover.
 *
 * Kept small on purpose: the full inbox lives on the IdentityPage under a
 * "Mentions" section.
 */
export function NotificationBell() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const list = notificationsSignal.value;
  const unread = unreadCountSignal.value;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (e.target instanceof Node && rootRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const tone: 'muted' | 'has-unread' = unread === 0 ? 'muted' : 'has-unread';
  const label = unread === 0 ? '0' : String(unread);
  const title = unread === 0 ? 'Keine ungelesenen Erwähnungen' : `${unread} ungelesene Erwähnung${unread === 1 ? '' : 'en'}`;

  const openItem = (n: NotificationDto) => {
    // Fire-and-forget: user clicked = they've seen it. The route change
    // below happens regardless.
    if (!n.seenAt) void markNotificationSeen(n.id);
    setOpen(false);
    if (n.sourceKind === 'ChartAnnotation') {
      location.route(`/plants/${n.plantId}/charts?annotation=${n.sourceId}`);
    }
  };

  const top = list.slice(0, 10);

  return (
    <div ref={rootRef} class="hv-notif-wrap" style={{ position: 'relative' }}>
      <button
        type="button"
        class={`hv-notif-bell hv-notif-bell--${tone}`}
        aria-label={title}
        title={title}
        onClick={() => setOpen((o) => !o)}
      >
        <span class="hv-notif-bell__icon" aria-hidden="true">
          {/* Envelope glyph — distinct from the alarm bell so operators tell
              them apart at a glance. Same 12x12 as AlarmIndicator. */}
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path
              d="M2.5 3.5h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Zm0 1v.5l5.5 3.6 5.5-3.6v-.5h-11Z"
              fill="currentColor"
            />
          </svg>
        </span>
        <span class="hv-notif-bell__count">{label}</span>
      </button>
      {open && (
        <div class="hv-notif-drop" role="menu">
          <div class="hv-notif-drop__header">
            <span>Erwähnungen</span>
            {unread > 0 && (
              <button
                type="button"
                class="hv-btn hv-btn--sm hv-btn--ghost"
                onClick={(e) => { e.stopPropagation(); void markAllNotificationsSeen(); }}
              >
                Alle gelesen
              </button>
            )}
          </div>
          {top.length === 0 ? (
            <div class="hv-notif-drop__empty">Noch keine Erwähnungen.</div>
          ) : (
            top.map((n) => (
              <button
                key={n.id}
                type="button"
                class={`hv-notif-drop__item${n.seenAt ? '' : ' hv-notif-drop__item--unread'}`}
                onClick={() => openItem(n)}
              >
                <div>{n.previewText || '(kein Vorschautext)'}</div>
                <div class="hv-notif-drop__meta">
                  {formatWhen(n.createdAt)} · {n.sourceKind === 'ChartAnnotation' ? 'Chart-Notiz' : n.sourceKind}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const delta = Math.max(0, now - d.getTime());
  const min = Math.floor(delta / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `vor ${days} d`;
  return d.toLocaleDateString();
}
